/**
 * FeatureMatrix - Feature comparison grid across plans
 *
 * Features:
 * - Sticky first column with feature names
 * - Check/X icons for boolean features
 * - Number display for limits
 * - Horizontal scroll for many plans
 */
import { cn } from '@respectly/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@respectly/ui';
import { Check, X, Minus } from 'lucide-react';
import type { PlanWithStats } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface FeatureMatrixProps {
  plans: PlanWithStats[];
  className?: string;
}

interface FeatureRow {
  name: string;
  key: string;
  type: 'boolean' | 'number' | 'text';
  getValue: (plan: PlanWithStats) => boolean | number | string;
  format?: (value: number) => string;
}

// ============================================
// Configuration
// ============================================

const FEATURES: FeatureRow[] = [
  {
    name: 'Monthly Price',
    key: 'monthlyPrice',
    type: 'number',
    getValue: (plan) => plan.monthlyPrice,
    format: (v) => `$${v}`,
  },
  {
    name: 'Annual Price',
    key: 'annualPrice',
    type: 'number',
    getValue: (plan) => plan.annualPrice,
    format: (v) => `$${v}`,
  },
  {
    name: 'SMS Credits/Month',
    key: 'smsCreditsPerMonth',
    type: 'number',
    getValue: (plan) => plan.smsCreditsPerMonth,
    format: (v) => v.toLocaleString(),
  },
  {
    name: 'Email Credits/Month',
    key: 'emailCreditsPerMonth',
    type: 'number',
    getValue: (plan) => plan.emailCreditsPerMonth,
    format: (v) => v.toLocaleString(),
  },
  {
    name: 'Active Status',
    key: 'isActive',
    type: 'boolean',
    getValue: (plan) => plan.isActive,
  },
  {
    name: 'Current Subscribers',
    key: 'subscriberCount',
    type: 'number',
    getValue: (plan) => plan.subscriberCount,
    format: (v) => v.toLocaleString(),
  },
  {
    name: 'MRR Contribution',
    key: 'mrrContribution',
    type: 'number',
    getValue: (plan) => plan.mrrContribution,
    format: (v) => `$${v.toLocaleString()}`,
  },
];

// ============================================
// Cell Renderers
// ============================================

function BooleanCell({ value }: { value: boolean }) {
  if (value) {
    return (
      <div className="flex items-center justify-center">
        <div className="h-6 w-6 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
          <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center">
      <div className="h-6 w-6 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
        <X className="h-3.5 w-3.5 text-slate-400" />
      </div>
    </div>
  );
}

function NumberCell({ value, format }: { value: number; format?: (v: number) => string }) {
  const formatted = format ? format(value) : value.toString();

  if (value === 0) {
    return (
      <div className="flex items-center justify-center">
        <Minus className="h-4 w-4 text-slate-300 dark:text-slate-600" />
      </div>
    );
  }

  return (
    <span className="text-sm font-medium text-slate-900 dark:text-white tabular-nums">
      {formatted}
    </span>
  );
}

function TextCell({ value }: { value: string }) {
  return (
    <span className="text-sm text-slate-700 dark:text-slate-300 truncate">
      {value || <Minus className="h-4 w-4 text-slate-300 dark:text-slate-600 inline" />}
    </span>
  );
}

// ============================================
// Component
// ============================================

export function FeatureMatrix({ plans, className }: FeatureMatrixProps) {
  if (plans.length === 0) {
    return (
      <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
        <CardContent className="flex items-center justify-center py-16">
          <p className="text-sm text-slate-500">No plans to compare</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('border-slate-200/60 dark:border-slate-700/60 overflow-hidden', className)}>
      <CardHeader className="pb-0">
        <CardTitle className="text-base font-semibold">Feature Comparison</CardTitle>
      </CardHeader>
      <CardContent className="p-0 mt-4">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                {/* Feature name column (sticky) */}
                <th
                  className="sticky left-0 z-10 bg-white dark:bg-slate-900 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 min-w-[180px]"
                >
                  Feature
                </th>
                {/* Plan columns */}
                {plans.map((plan) => (
                  <th
                    key={plan.id}
                    className="px-4 py-3 text-center min-w-[140px]"
                  >
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-sm font-semibold text-slate-900 dark:text-white">
                        {plan.name}
                      </span>
                      <span
                        className={cn(
                          'text-[10px] px-1.5 py-0.5 rounded-full',
                          plan.isActive
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                            : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                        )}
                      >
                        {plan.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {FEATURES.map((feature, rowIndex) => (
                <tr
                  key={feature.key}
                  className={cn(
                    'transition-colors',
                    rowIndex % 2 === 0
                      ? 'bg-white dark:bg-slate-900'
                      : 'bg-slate-50/50 dark:bg-slate-800/30'
                  )}
                >
                  {/* Feature name (sticky) */}
                  <td
                    className={cn(
                      'sticky left-0 z-10 px-4 py-3 text-sm text-slate-600 dark:text-slate-400',
                      rowIndex % 2 === 0
                        ? 'bg-white dark:bg-slate-900'
                        : 'bg-slate-50/50 dark:bg-slate-800/30'
                    )}
                  >
                    {feature.name}
                  </td>
                  {/* Plan values */}
                  {plans.map((plan) => {
                    const value = feature.getValue(plan);
                    return (
                      <td
                        key={plan.id}
                        className="px-4 py-3 text-center"
                      >
                        {feature.type === 'boolean' && (
                          <BooleanCell value={value as boolean} />
                        )}
                        {feature.type === 'number' && (
                          <NumberCell value={value as number} format={feature.format} />
                        )}
                        {feature.type === 'text' && (
                          <TextCell value={value as string} />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================
// Skeleton
// ============================================

export function FeatureMatrixSkeleton({ className }: { className?: string }) {
  return (
    <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
      <CardHeader className="pb-0">
        <div className="h-5 w-40 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
      </CardHeader>
      <CardContent className="p-0 mt-4">
        <div className="overflow-x-auto">
          <div className="h-[400px] bg-slate-100 dark:bg-slate-800 animate-pulse" />
        </div>
      </CardContent>
    </Card>
  );
}
